import { getAgentApi } from '@/gql';
import user from '@/stores/user';
import { useEffect, useState } from 'react';

export interface useChatLimitResult {
    chatLimit: number;
    chatCount: number;
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<{ limit: number; count: number }>;
}

export const useChatLimit = (uId: string): useChatLimitResult => {
    const [chatLimit, setChatLimit] = useState<number>(0);
    const [chatCount, setChatCount] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const { userId } = user;

    const getChatLimit = async () => {
        if (!uId || !userId) {
            setChatLimit(0);
            setChatCount(0);
            return {
                limit: 0,
                count: 0,
            };
        }
        try {
            setLoading(true);
            setError(null);
            const api = getAgentApi();
            // Use combined query to fetch both limit and count in a single request
            const response = await api.chatLimitInfo({ agentUid: uId });
            if (response) {
                setChatLimit(response.dailyUserChatsLimit ?? 0);
                setChatCount(response.countUserChatsToday ?? 0);
                return {
                    limit: response.dailyUserChatsLimit ?? 0,
                    count: response.countUserChatsToday ?? 0,
                };
            } else {
                setChatLimit(0);
                setChatCount(0);
                return {
                    limit: 0,
                    count: 0,
                };
            }
        } catch (err) {
            console.error('Failed to fetch chat limit:', err);
            setError(
                err instanceof Error
                    ? err
                    : new Error('Failed to fetch chat limit'),
            );
            setChatLimit(0);
            setChatCount(0);
            return {
                limit: 0,
                count: 0,
            };
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getChatLimit();
    }, [uId, userId]);

    return {
        chatLimit,
        chatCount,
        loading,
        error,
        refetch: getChatLimit,
    };
};
